import type { PortalRecipePayload } from '../portal/portalDraftEnvelope';

/** Predefined course for recipe editor (single-select). */
export const RECIPE_COURSES = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Snack',
  'Appetizer',
  'Soup',
  'Salad',
  'Main',
  'Side',
  'Dessert',
  'Baking',
  'Beverage',
  'Other',
] as const;

/** Two balanced rows for the recipe editor (7 + 6). */
export const RECIPE_COURSES_ROW1 = RECIPE_COURSES.slice(0, 7);
export const RECIPE_COURSES_ROW2 = RECIPE_COURSES.slice(7);

export const RECIPE_DIFFICULTIES = ['Easy', 'Medium', 'Hard'] as const;

/** Prep time labels (minutes) shown as sorted chips; `120+` maps to 121 in JSON for "over 2h". */
export const RECIPE_PREP_LABELS = ['15', '30', '45', '60', '90', '120', '120+'] as const;

export type RecipePrepLabel = (typeof RECIPE_PREP_LABELS)[number];

export function prepMinutesFromLabel(label: string): number {
  if (label === '120+') return 121;
  const n = parseInt(label, 10);
  return Number.isFinite(n) ? n : 0;
}

export function prepLabelFromMinutes(minutes: number): RecipePrepLabel | '' {
  if (minutes == null || minutes <= 0) return '';
  if (minutes > 120) return '120+';
  const hit = (['15', '30', '45', '60', '90', '120'] as const).find((x) => x === String(minutes));
  return (hit || '') as RecipePrepLabel | '';
}

/** Servings options: label shown in UI, value stored in `servings`. */
export const RECIPE_SERVINGS: { label: string; value: number }[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '8', value: 8 },
  { label: '10', value: 10 },
  { label: '12', value: 12 },
  { label: '16', value: 16 },
  { label: '20+', value: 24 },
];

export function servingsLabelForValue(servings: number): string {
  const row = RECIPE_SERVINGS.find((r) => r.value === servings);
  if (row) return row.label;
  if (servings >= 20) return '20+';
  return '';
}

/** Normalize legacy lowercase course/difficulty to our chip labels. */
export function normalizeCourseLabel(raw: string | undefined): string {
  const t = (raw || '').trim();
  if (!t) return '';
  const lower = t.toLowerCase();
  const found = RECIPE_COURSES.find((c) => c.toLowerCase() === lower);
  if (found) return found;
  return (RECIPE_COURSES as readonly string[]).includes(t) ? t : '';
}

export function normalizeDifficultyLabel(raw: string | undefined): string {
  const t = (raw || '').trim();
  if (!t) return '';
  const lower = t.toLowerCase();
  const found = RECIPE_DIFFICULTIES.find((d) => d.toLowerCase() === lower);
  return found || '';
}

export function difficultySliderIndex(label: string): number {
  const n = normalizeDifficultyLabel(label);
  const i = (RECIPE_DIFFICULTIES as readonly string[]).indexOf(n);
  return i >= 0 ? i : 1;
}

export function prepSliderIndex(label: RecipePrepLabel | ''): number {
  if (!label) return 1;
  const i = (RECIPE_PREP_LABELS as readonly string[]).indexOf(label as RecipePrepLabel);
  return i >= 0 ? i : 1;
}

export function servingsSliderIndex(label: string): number {
  const i = RECIPE_SERVINGS.findIndex((r) => r.label === label);
  return i >= 0 ? i : 3;
}

/** Title, image, ingredients, steps, course, difficulty, prep preset, and servings preset must all be set. */
export function recipePayloadMeetsMandatoryFields(r: PortalRecipePayload): boolean {
  if (!r.title?.trim()) return false;
  if (!(r.image || '').trim()) return false;
  if (!r.ingredients?.some((x) => (x || '').trim())) return false;
  if (!r.steps?.some((x) => (x || '').trim())) return false;
  if (!normalizeCourseLabel(r.course)) return false;
  if (!normalizeDifficultyLabel(r.difficulty)) return false;
  if (!prepLabelFromMinutes(r.prepMinutes)) return false;
  if (!servingsLabelForValue(r.servings)) return false;
  return true;
}
